// Shared off-screen visibility gate for cheap, list-safe CSS animations
// (Poola completion sticker, P3-A). A Poola offers list can render dozens of
// completed cards at once — giving each one its own `IntersectionObserver`
// works, but a SINGLE shared observer instance (this module) keeps the cost
// flat regardless of list size. Consumers get back a template ref + a
// reactive `isVisible` boolean; CSS then toggles `animation-play-state` off
// the boolean instead of running anything (rAF, timers) while off-screen.
import { onBeforeUnmount, onMounted, ref } from 'vue';

let sharedObserver = null;
const callbacksByElement = new WeakMap();

function getSharedObserver() {
  if (sharedObserver) return sharedObserver;
  if (typeof IntersectionObserver === 'undefined') return null;
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const onChange = callbacksByElement.get(entry.target);
        if (onChange) onChange(entry.isIntersecting);
      }
    },
    { rootMargin: '80px 0px' },
  );
  return sharedObserver;
}

/**
 * @returns {{ el: import('vue').Ref, isVisible: import('vue').Ref<boolean> }}
 *   Bind `el` to a template ref on the element to watch; `isVisible` reflects
 *   whether it currently intersects the viewport (defaults to true so
 *   environments without IntersectionObserver, e.g. some test runners,
 *   never wrongly suppress the animation).
 */
export function useOffscreenAnimationGate() {
  const el = ref(null);
  const isVisible = ref(true);

  onMounted(() => {
    const observer = getSharedObserver();
    if (!observer || !el.value) return;
    callbacksByElement.set(el.value, (visible) => {
      isVisible.value = visible;
    });
    observer.observe(el.value);
  });

  onBeforeUnmount(() => {
    if (!sharedObserver || !el.value) return;
    sharedObserver.unobserve(el.value);
    callbacksByElement.delete(el.value);
  });

  return { el, isVisible };
}
